import type { PropsWithChildren } from 'react';
import {
  useCallback, useContext, useEffect, useState,
} from 'react';
import * as React from 'react';

import {
  Alert, Button, CircularProgress, Container, Snackbar,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { type Transcription, TranscriptionService } from '@klangio/ai2notes-ts-client';
import { makeStyles } from 'tss-react/mui';
import { FunctionVideo } from '../../components/molecules';
import AdminDetails from '../../components/molecules/AdminDetails';
import { InputContainer } from '../../components/organisms';
import PricingTableModal from '../../components/templates/PricingTableModal';
import {
  AppConstantsContext,
  ExportTranscriptionContext,
  NavigationContext,
  TranscriptionStoreContext,
  View,
  type WithUser,
  withUserContext,
} from '../../context';
import { TranscriptionState } from '../../entities';
import useProcessingTranscriptionUpdate from '../../hooks/useProcessingTranscriptionUpdate';
import { type TranscriptionFinishData, type TranscriptionProgressData } from '../../services';
import { type ITheme } from '../../theme';
import { getErrorMessage, hasFailed, storeTranscriptionIdFromUnkownUsers } from '../../utils';
import FileProgress from './FileProgress';
import FileWrapper from './FileWrapper';

export const DELAY_RATING_MODAL_TIME_IN_MS = 20 * 1000;

const useStyles = makeStyles()((theme: ITheme) => ({
  preamble: {
    marginBottom: 50,
    marginTop: 50,
  },
  functionImage: {
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '80%',
    maxWidth: '100%',
    height: 'auto',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
}));

export interface FileProps extends WithUser, PropsWithChildren {
  musicJson: any;
  fetchTranscriptionFiles: (transcription: Transcription) => void;
  setOpenPricingModal: (open: boolean) => void;
  openPricingModal: boolean;
}

const File: React.FC<FileProps> = ({
  musicJson,
  user,
  fetchTranscriptionFiles,
  children,
  setOpenPricingModal,
  openPricingModal,
  isLoadingUserCompleted,
}) => {
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const tag = '[File] -';
  const { t } = useTranslation();
  const processingTimer = 0;
  const exportErrorMessage: string = null;

  const { clearError } = React.useContext(ExportTranscriptionContext);
  const { classes } = useStyles();
  const { setCurrentView } = useContext(NavigationContext);

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const { selectedTranscription, setSelectedTranscription } = useContext(TranscriptionStoreContext);
  const [processing, setProcessing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [processingTime, setProcessingTime] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [estimatedDuration, setEstimatedDuration] = useState<number>(180);

  const { awConversionId, jobType, title } = useContext(AppConstantsContext);
  const [notFound, setNotFound] = useState<boolean>(false);
  // const [unlockError, setUnlockError] = useState<string | null>(null);
  const [jobState, setJobState] = useState(null);

  const handleProgressUpdate = useCallback(
    (data: TranscriptionProgressData) => {
      setProgress(data.progress);
      setMessage(data.message);
      setProcessingTime(processingTime + 1);
      setJobState(data.jobState);
      if (
        data.jobState === TranscriptionState.Processing
        && jobState === TranscriptionState.Enqueued
      ) {
        setEstimatedDuration(estimatedDuration + processingTime);
      }

      if (
        data.jobState === TranscriptionState.Error
        || data.jobState === TranscriptionState.Refunded
      ) {
        if (processingTimer) clearInterval(processingTimer);
        TranscriptionService.transcriptionGetGet(id).then((transcriptionData) =>
          setSelectedTranscription(transcriptionData));
      }
    },
    [processingTime, jobState, estimatedDuration, id, processingTimer],
  );

  const handleFinishedTranscription = useCallback(
    (data: TranscriptionFinishData) => {
      if (processingTimer) clearInterval(processingTimer);
      TranscriptionService.transcriptionGetGet(data.id).then((transcriptionData: Transcription) => {
        setSelectedTranscription(transcriptionData);
        setProcessing(false);

        if (transcriptionData.state === TranscriptionState.Ok) {
          fetchTranscriptionFiles(transcriptionData);
        }
      });
    },
    [fetchTranscriptionFiles, processingTimer],
  );

  const { count: progressCounter, startUpdating } = useProcessingTranscriptionUpdate(
    id,
    handleProgressUpdate,
    handleFinishedTranscription,
  );

  useEffect(() => {
    setCurrentView(View.display);
    TranscriptionService.transcriptionGetGet(id)
      .then((transcr: Transcription) => {
        document.title = `${transcr.title} - ${title}`;
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            send_to: awConversionId,
            transaction_id: transcr.id,
          });
        }
        setSelectedTranscription(transcr);
        if (
          transcr.state === TranscriptionState.Enqueued
          || transcr.state === TranscriptionState.Processing
        ) {
          // eslint-disable-next-line no-use-before-define
          analysisStarted();
        } else if (transcr.state === TranscriptionState.Ok) {
          console.log('fetching');
          fetchTranscriptionFiles(transcr);
        }
      })
      .catch((e) => {
        console.log(e);
        setNotFound(true);
      });
    return () => {
      if (processingTimer) {
        clearInterval(processingTimer);
      }
    };
  }, []);

  if (isLoadingUserCompleted && !user && id) {
    storeTranscriptionIdFromUnkownUsers(id);
  }

  const analysisStarted = useCallback(() => {
    console.log('analysisStarted');
    setProcessing(true);
    startUpdating();
    setProcessingTime(0);
    setMessage(t('home.input.in_queue'));
    setProgress(0);
  }, [startUpdating]);

  return (
    <FileWrapper
      transcription={selectedTranscription}
      pricingModal={
        openPricingModal && (
          <PricingTableModal
            open={openPricingModal}
            handleClose={() => setOpenPricingModal(false)}
            transcription={selectedTranscription}
          />
        )
      }
    >
      {processing && (
        <FileProgress
          transcription={selectedTranscription}
          message={message}
          progress={progress}
          estimatedDuration={estimatedDuration}
          processingTime={progressCounter}
        />
      )}

      {!processing && (
        <>
          <Snackbar
            autoHideDuration={6000}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={exportErrorMessage !== null}
            onClose={clearError}
            key="exportErrorMsg"
          >
            <Alert
              action={(
                <Button onClick={clearError} variant="text" size="small">
                  {t('home.songbook.error.download_failed_btn_txt')}
                </Button>
              )}
              severity="error"
            >
              {exportErrorMessage}
            </Alert>
          </Snackbar>
          {!notFound && !musicJson && !hasFailed(selectedTranscription) && (
            <CircularProgress color="inherit" style={{ color: '#ffffff', margin: 50 }} />
          )}
          {!musicJson && (
            <Container maxWidth="md" className={classes.preamble}>
              <FunctionVideo
                className={classes.functionImage}
                videoSrc={`/img/${jobType}/function_cropped.webm`}
                altImgSrc={`/img/${jobType}/function_mobile.png`}
              />
            </Container>
          )}

          {hasFailed(selectedTranscription) && (
            <>
              <Container maxWidth="sm" style={{ paddingBottom: 30 }}>
                <Alert elevation={6} variant="filled" severity="error">
                  {getErrorMessage(selectedTranscription.error, selectedTranscription.source, t)}
                </Alert>
              </Container>
              <InputContainer another />
            </>
          )}

          {notFound && (
            <>
              <Container maxWidth="sm" style={{ paddingBottom: 30 }}>
                <Alert elevation={6} variant="filled" severity="error">
                  {t('file.error.not_found')}
                </Alert>
              </Container>
              <InputContainer another />
            </>
          )}

          {children}
        </>
      )}
      {selectedTranscription
        && selectedTranscription?.state !== TranscriptionState.Ok
        && user?.role === 'admin' && (
          <div style={{ padding: 20 }}>
            <AdminDetails transcription={selectedTranscription} />
          </div>
      )}
    </FileWrapper>
  );
};

export default withUserContext(File);
